import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { numberFormat } from "web/script/modules/formats";
import analytics from "web/script/analytics/analytics";
import { productFeedLoadMoreContent } from "web/script/components/product-feed";
import { gettext } from "web/script/modules/django-i18n";
import SecondaryButton from "web/react/components/buttons/secondary-button/secondary-button";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import history from "web/script/utils/history";
import withReduxProvider from "web/react/redux-provider";
import { hideSeoContent } from "web/redux/ducks/feed-seo-content";
import store from "web/redux/store";
import {
    setButtonNextUrl,
    setVisibleProductCount,
    setRemainingProductCount,
} from "web/redux/ducks/feed-show-more";
import url from "web/script/utils/url";
import styles from "./feeds-show-more.css";

const PRODUCTS_PER_PAGE = 42;

function mapStateToProps(state) {
    const feedstate = state.feedShowMoreReducer;
    return {
        visibleProductCount: feedstate.visible_product_count,
        totalProductCount: feedstate.total_product_count,
        remainingProductCount: feedstate.remaining_product_count,
        buttonNextUrl: feedstate.show_more_next_url,
        buttonText: feedstate.show_more_text,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        setButtonNextUrl: (url) => {
            dispatch(setButtonNextUrl(url));
        },
        setVisibleProductCount: (count) => {
            dispatch(setVisibleProductCount(count));
        },
        setRemainingProductCount: (count) => {
            dispatch(setRemainingProductCount(count));
        },
    };
}

function ProgressBar({ currentNumber, maximumNumber }) {
    const progress = currentNumber / maximumNumber;
    return (
        <div className={styles.progressBar} role="presentation">
            <span
                className={styles.inner}
                style={{
                    width: progress * 100 + "%",
                }}
            />
        </div>
    );
}

export function FeedsShowMore({
    visibleProductCount,
    totalProductCount,
    remainingProductCount,
    buttonNextUrl,
    buttonText,
    setButtonNextUrl,
    setVisibleProductCount,
    setRemainingProductCount,
}) {
    const [isLoading, setIsLoading] = React.useState(false);

    let page = 1;
    let remainingProducts = remainingProductCount ? remainingProductCount : totalProductCount;

    // feed was reset
    if (visibleProductCount <= PRODUCTS_PER_PAGE) {
        if (buttonNextUrl) {
            let parsedUrl = url.parse(buttonNextUrl);
            page = parseInt(parsedUrl.searchParams.get("page"), 10) - 1;
            remainingProducts = totalProductCount - (page - 1) * PRODUCTS_PER_PAGE;
        } else {
            remainingProducts = visibleProductCount;
        }
    }

    let messageText = null;
    if (totalProductCount > 0 || visibleProductCount == 0) {
        // "Showing {current_product_count} of {total_product_count}"
        messageText = gettext("feed_pagination.summary_showing_products", {
            current_product_count: numberFormat(visibleProductCount),
            total_product_count: numberFormat(remainingProducts),
        });
    }

    function onClickButton() {
        setIsLoading(true);
        analytics.event("feeds_show_more", "click_show_more_button", "click");
        const loadMorePromise = productFeedLoadMoreContent(buttonNextUrl);
        loadMorePromise.then(function(newButtonNextUrl) {
            history.pushState(buttonNextUrl, "", null, null, true, true);
            setButtonNextUrl(newButtonNextUrl);
            setVisibleProductCount(
                Math.min(visibleProductCount + PRODUCTS_PER_PAGE, remainingProducts)
            );
            setRemainingProductCount(remainingProducts);
            document.activeElement.blur();
            setIsLoading(false);
            store.dispatch(hideSeoContent());
        });
    }

    let loadingHtml = null;
    if (isLoading) {
        loadingHtml = (
            <div className={styles.loadingWrapper}>
                <LoadingSpinner />
            </div>
        );
    }

    let buttonHtml = null;
    if (buttonNextUrl) {
        buttonHtml = <SecondaryButton onClick={onClickButton}>{buttonText}</SecondaryButton>;
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.loadingWrapper}>{loadingHtml}</div>

            <div className={styles.progressMessage}>
                <p>{messageText}</p>
            </div>

            <ProgressBar currentNumber={visibleProductCount} maximumNumber={remainingProducts} />

            <div className={styles.showMoreButton}>{buttonHtml}</div>
        </div>
    );
}

FeedsShowMore.propTypes = {
    visibleProductCount: PropTypes.number,
    totalProductCount: PropTypes.number,
    remainingProductCount: PropTypes.number,
    buttonNextUrl: PropTypes.string,
    buttonText: PropTypes.string,
    setButtonNextUrl: PropTypes.func,
    setVisibleProductCount: PropTypes.func,
    setRemainingProductCount: PropTypes.func,
};

ProgressBar.propTypes = {
    currentNumber: PropTypes.number,
    maximumNumber: PropTypes.number,
};

ProgressBar.defaultProps = {
    currentNumber: 0,
    maximumNumber: 1,
};

export default withReduxProvider(connect(mapStateToProps, mapDispatchToProps)(FeedsShowMore));
